import { useState } from 'react';
import {
  Alert,
  AttachmentField,
  Button,
  DateField,
  Field,
  FormRow,
  Modal,
  ModalActions,
  SearchSelect,
  Select,
  TextArea,
  readAttachmentContent,
  useAttachments,
  type AttachmentRestriction,
} from '@evogard/ui';
import {
  PIX_KEY_TYPES,
  SALE_EXPENSE_ATTACHMENT,
  SALE_EXPENSE_ATTACHMENT_HELP,
  SALE_EXPENSE_FORMS,
  SALE_EXPENSE_REASON_MAX,
  SALE_EXPENSE_TEXT_MAX,
  SALE_EXPENSE_TYPES,
  SALE_EXPENSE_TYPE_LABELS,
  businessDate,
  cents,
  checkPixKey,
  checkSaleExpense,
  checkSaleExpenseAttachment,
  pixKeyTypeOf,
  toColumn,
  type PixKeyType,
  type SaleExpenseField,
  type SaleExpenseType,
} from '@evogard/domain';
import type { Sale, SaleExpenseDetail } from '@evogard/contract';
import { BANKS } from '../../components/beneficiary/banks.js';
import { PIX_PLACEHOLDERS, PIX_TYPE_LABELS } from '../../components/beneficiary/labels.js';
import { api, errorMessage } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';

/**
 * O anexo da despesa aceita menos que a casa: foto ou PDF, com o teto de cada
 * um. As páginas do PDF quem conta é a API.
 */
const ATTACHMENT_RULE: AttachmentRestriction = {
  accept: [...SALE_EXPENSE_ATTACHMENT.imageTypes, 'application/pdf'],
  sentence: SALE_EXPENSE_ATTACHMENT_HELP,
  check: (typeMime, sizeBytes) => checkSaleExpenseAttachment({ typeMime, sizeBytes }),
};

/** A lista do BACEN, montada uma vez — a mesma do cadastro de beneficiário. */
const BANK_OPTIONS = BANKS.map((b) => ({ value: b.code, label: `${b.code} · ${b.name}` }));

/**
 * O que o formulário abre: o lançamento a partir de uma venda, ou a correção
 * de uma despesa devolvida (o tipo e a venda não mudam na correção).
 */
export type SaleExpenseFormMode =
  | { kind: 'lancar'; sale: Sale }
  | { kind: 'corrigir'; expense: SaleExpenseDetail };

/**
 * O formulário da despesa da venda — um por tipo, com os campos que
 * `SALE_EXPENSE_FORMS` diz (02/10/2026).
 *
 * ## O que nasce preenchido
 *
 * A data (hoje, menos no reembolso, que pede a data da venda), a placa (a do
 * veículo da venda), o nome (o associado da venda, nos tipos que são sobre
 * ele) e o consultor — que é quem está logado, e não se troca: a despesa é de
 * quem a lança, e é ele que o Financeiro avisa.
 *
 * ## O anexo é conferido aqui e de novo no servidor
 *
 * A tela recusa na escolha o que não é foto nem PDF e o que passa do tamanho;
 * as PÁGINAS do PDF quem conta é a API (`pdfPageCount`) — a tela não abre o
 * arquivo. A frase de recusa é a mesma dos dois lados (`checkSaleExpenseAttachment`).
 */
export function SaleExpenseFormModal({
  mode,
  onClose,
  onSaved,
}: {
  mode: SaleExpenseFormMode;
  onClose: () => void;
  onSaved: (detail: SaleExpenseDetail) => void;
}) {
  const { data: context } = useSessionContext();
  const correcting = mode.kind === 'corrigir' ? mode.expense : null;
  const sale = mode.kind === 'lancar' ? mode.sale : null;

  const [type, setType] = useState<SaleExpenseType | ''>(correcting?.type ?? '');
  const form = type === '' ? null : SALE_EXPENSE_FORMS[type];

  const memberName = sale?.member?.nome ?? '';
  const [referenceDate, setReferenceDate] = useState(correcting?.referenceDate ?? '');
  const [plate, setPlate] = useState(correcting?.plate ?? sale?.vehicle?.placa ?? '');
  const [personName, setPersonName] = useState(correcting?.personName ?? '');
  const [amount, setAmount] = useState(correcting?.amount.replace('.', ',') ?? '');
  const [pixKeyType, setPixKeyType] = useState<PixKeyType | ''>(
    pixKeyTypeOf(correcting?.pixKey) ?? '',
  );
  const [pixKey, setPixKey] = useState(correcting?.pixKey ?? '');
  const [pixBankCode, setPixBankCode] = useState(correcting?.pixBankCode ?? '');
  const [contact, setContact] = useState(correcting?.contact ?? '');
  const [cancellationReason, setCancellationReason] = useState(
    correcting?.cancellationReason ?? '',
  );
  const attachment = useAttachments();

  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* Escolher o tipo preenche o que ele pede — e só o que ainda está vazio. */
  const chooseType = (next: SaleExpenseType | '') => {
    setType(next);
    if (next === '') return;
    const f = SALE_EXPENSE_FORMS[next];
    if (referenceDate === '' && f.dateDefaultsToToday) {
      setReferenceDate(businessDate(new Date()));
    }
    if (personName === '' && f.personFromMember) setPersonName(memberName);
  };

  const amountCents = centsOf(amount);
  const errors: Partial<Record<SaleExpenseField, string>> =
    type === ''
      ? {}
      : checkSaleExpense({
          type,
          referenceDate,
          plate,
          personName,
          amountCents,
          pixKeyType,
          pixKey,
          pixBankCode,
          contact,
          cancellationReason,
        });
  const needsAttachment = correcting === null && attachment.items.length === 0;
  /*
   * A despesa é do consultor DA VENDA, e não de quem lança: o administrador que
   * lança pela venda de outra pessoa não vira dono dela. Venda sem consultor
   * não recebe despesa — não haveria a quem pertencer (a API recusa igual).
   */
  const consultantName = correcting?.consultant.nome ?? sale?.consultant?.user.nome ?? null;
  const valid =
    consultantName !== null &&
    type !== '' &&
    Object.keys(errors).length === 0 &&
    !needsAttachment;
  const shown = (field: SaleExpenseField) => (tried ? errors[field] : undefined);

  /* A recusa por tipo e tamanho é do campo (`ATTACHMENT_RULE`), com o diálogo da casa. */
  const addAttachment = (files: File[], typesMime: string[]) => {
    const file = files[0];
    const typeMime = typesMime[0];
    if (!file || !typeMime) return;
    attachment.clear();
    attachment.add([file], [typeMime]);
  };

  const submit = async () => {
    setTried(true);
    if (!valid || !form) return;
    setBusy(true);
    setError(null);
    try {
      const chosen = attachment.items[0];
      const file = chosen
        ? {
            nome: chosen.nome,
            typeMime: chosen.typeMime,
            content: await readAttachmentContent(chosen.file),
          }
        : undefined;
      const normalizedPix =
        form.pix && pixKeyType !== '' ? checkPixKey(pixKeyType, pixKey) : null;
      const fields = {
        referenceDate,
        plate,
        personName: personName.trim(),
        amount: toColumn(cents(amountCents!)),
        pixKey: normalizedPix?.valid ? normalizedPix.normalized : null,
        pixBankCode: form.pix ? pixBankCode : null,
        contact: form.contact ? contact.trim() : null,
        cancellationReason: form.cancellationReason ? cancellationReason.trim() : null,
      };

      const r = correcting
        ? await api.saleExpenses.correct({
            params: { id: correcting.id },
            body: { ...fields, attachment: file },
          })
        : await api.saleExpenses.create({
            body: { saleId: sale!.id, type: type as SaleExpenseType, ...fields, attachment: file! },
          });
      if (r.status !== 200 && r.status !== 201) throw new Error(errorMessage(r.body));
      onSaved(r.body as SaleExpenseDetail);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Algo deu errado.');
    } finally {
      setBusy(false);
    }
  };

  const vehicle = sale?.vehicle
    ? [sale.vehicle.placa, sale.vehicle.marca, sale.vehicle.modelo].filter(Boolean).join(' · ')
    : null;

  return (
    <Modal
      open
      onOpenChange={(open) => !open && !busy && onClose()}
      title={correcting ? 'Corrigir a despesa' : 'Lançar despesa'}
      descricao={
        correcting
          ? `${SALE_EXPENSE_TYPE_LABELS[correcting.type]} — volta para "Em Conferência" quando você salvar.`
          : [
              memberName || (sale?.codigoAssociado ? `associado ${sale.codigoAssociado}` : null),
              vehicle,
            ]
              .filter(Boolean)
              .join(' · ') || 'Uma despesa sobre esta venda.'
      }
      size="lg"
    >
      <form
        className="flex flex-col gap-4"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {correcting?.correctionReason && (
          <Alert tone="alerta" title="O que o Financeiro pediu">
            {correcting.correctionReason}
          </Alert>
        )}

        {consultantName === null && (
          <Alert tone="alerta" title="Esta venda ainda não tem consultor">
            A despesa é do consultor da venda. Case a venda com o consultor em
            “Corrigir” e lance a despesa depois.
          </Alert>
        )}

        {!correcting && (
          <Select
            label="Tipo de despesa"
            required
            value={type}
            onChange={(e) => chooseType(e.target.value as SaleExpenseType | '')}
            error={tried && type === '' ? 'Escolha o tipo de despesa.' : undefined}
          >
            <option value="">Escolha…</option>
            {SALE_EXPENSE_TYPES.map((t) => (
              <option key={t} value={t}>
                {SALE_EXPENSE_TYPE_LABELS[t]}
              </option>
            ))}
          </Select>
        )}

        {form && (
          <>
            <FormRow cols={[6, 6]}>
              <DateField
                label={form.dateLabel}
                required
                value={referenceDate}
                onChange={setReferenceDate}
                error={shown('referenceDate')}
              />
              <Field
                label="Placa"
                required
                maxLength={8}
                value={plate}
                onChange={(e) => setPlate(e.target.value.toUpperCase())}
                error={shown('plate')}
                help={sale?.vehicle?.placa ? 'Vem da venda; troque se for outro veículo.' : undefined}
              />
            </FormRow>

            <FormRow cols={form.contact ? [6, 6] : [12]}>
              <Field
                label={form.personLabel}
                required
                maxLength={SALE_EXPENSE_TEXT_MAX}
                value={personName}
                onChange={(e) => setPersonName(e.target.value)}
                error={shown('personName')}
              />
              {form.contact && (
                <Field
                  label="Contato"
                  required
                  maxLength={SALE_EXPENSE_TEXT_MAX}
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  error={shown('contact')}
                  help="Telefone ou e-mail de quem recebe o reembolso."
                />
              )}
            </FormRow>

            <FormRow cols={[6, 6]}>
              <Field
                label={form.amountLabel}
                required
                inputMode="decimal"
                prefix="R$"
                placeholder="0,00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                error={shown('amount')}
              />
              <Field
                label="Consultor"
                value={consultantName ?? 'a venda não tem consultor'}
                readOnly
                help={
                  sale && sale.consultant && sale.consultant.user.id !== context?.user.id
                    ? `O da venda — a despesa é dele, mesmo lançada por você.`
                    : 'O da venda. É quem acompanha a despesa e quem o Financeiro avisa.'
                }
              />
            </FormRow>

            {form.pix && (
              <>
                <FormRow cols={[4, 8]}>
                  <Select
                    label="Tipo da chave PIX"
                    required
                    value={pixKeyType}
                    onChange={(e) => setPixKeyType(e.target.value as PixKeyType | '')}
                    error={shown('pixKeyType')}
                  >
                    <option value="">Escolha…</option>
                    {PIX_KEY_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {PIX_TYPE_LABELS[t]}
                      </option>
                    ))}
                  </Select>
                  <Field
                    label={
                      type === 'indicacao'
                        ? 'Chave PIX do indicador'
                        : type === 'vistoria_extra'
                          ? 'Chave PIX do vistoriador'
                          : 'Chave PIX'
                    }
                    required
                    maxLength={SALE_EXPENSE_TEXT_MAX}
                    placeholder={pixKeyType ? PIX_PLACEHOLDERS[pixKeyType] : undefined}
                    value={pixKey}
                    onChange={(e) => setPixKey(e.target.value)}
                    error={shown('pixKey')}
                  />
                </FormRow>
                <SearchSelect
                  label="Banco de origem do PIX"
                  required
                  placeholder="Escolha o banco…"
                  searchPlaceholder="Buscar pelo nome ou pelo código…"
                  emptyMessage="Nenhum banco com esse nome na lista do BACEN."
                  options={BANK_OPTIONS}
                  value={pixBankCode}
                  onChange={setPixBankCode}
                  error={shown('pixBankCode')}
                />
              </>
            )}

            {form.cancellationReason && (
              <TextArea
                label="Motivo do cancelamento"
                required
                maxLength={SALE_EXPENSE_REASON_MAX}
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                error={shown('cancellationReason')}
              />
            )}

            <div className="flex flex-col gap-1.5">
              <AttachmentField
                label={`${form.attachmentLabel} (obrigatório)`}
                items={attachment.items}
                onAdd={addAttachment}
                onRemove={attachment.remove}
                max={1}
                disabled={busy}
                restrict={ATTACHMENT_RULE}
              />
              {correcting && (
                <p className="text-sm text-texto-suave">
                  Sem arquivo novo, fica o que você já enviou.
                </p>
              )}
              {tried && needsAttachment && (
                <p role="alert" className="text-sm text-erro">
                  Anexe o {form.attachmentLabel.toLowerCase()}.
                </p>
              )}
            </div>
          </>
        )}

        {error && <Alert tone="erro">{error}</Alert>}

        <ModalActions>
          <Button variant="discreto" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button type="submit" loading={busy}>
            {correcting ? 'Reenviar para conferência' : 'Lançar despesa'}
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
}

/** "1.234,56", "1234,56" e "1234.56" — em centavos; `null` quando não é número. */
function centsOf(text: string): number | null {
  let t = text.trim().replace(/\s/g, '');
  if (t === '') return null;
  if (t.includes(',')) t = t.replace(/\./g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  const [whole = '0', decimal = ''] = t.split('.');
  return Number(whole) * 100 + Number(decimal.padEnd(2, '0'));
}
